import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInventoryByVendor,
  updateProductStockAction,
} from "store/features/product/productActions";
import {
  selectProducts,
  selectProductsLoading,
} from "store/features/product/productSelectors";
import { FiEdit, FiSave, FiX, FiPlus, FiMinus } from "react-icons/fi";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const VendorInventory = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);
  const loading = useSelector(selectProductsLoading);

  const [editingProduct, setEditingProduct] = useState(null);
  const [stockValue, setStockValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch inventory data
  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      await dispatch(fetchInventoryByVendor());
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const handleEditClick = (product) => {
    setEditingProduct(product._id);
    setStockValue(product.stock);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setStockValue(0);
  };

  const handleStockChange = (value) => {
    if (value < 0) return;
    setStockValue(value);
  };

  const handleSaveStock = async (productId) => {
    try {
      await dispatch(updateProductStockAction(productId, stockValue));
      setEditingProduct(null);
      showSuccessToast("Stock updated successfully!");
      // Refresh data after update
      await fetchData();
    } catch (error) {
      showErrorToast("Failed to update stock");
    }
  };

  const incrementStock = () => handleStockChange(stockValue + 1);
  const decrementStock = () => handleStockChange(stockValue - 1);

  if (loading || isRefreshing) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto min-h-screen px-4 py-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Inventory Management
          </h1>
          <p className="mt-1 text-gray-600">
            {products?.length || 0} product{products?.length !== 1 ? "s" : ""}{" "}
            in inventory
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                  Stock
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {products.length > 0 ? (
                products.map((product) => (
                  <tr key={product._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-16 w-16 flex-shrink-0">
                          <img
                            className="h-16 w-16 rounded object-cover"
                            src={
                              product.mainImage?.[0] ||
                              "/placeholder-product.jpg"
                            }
                            alt={product.name}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900">
                        ₹{product.discountPrice || product.price}
                        {product.discountPrice && (
                          <span className="ml-2 text-sm text-gray-500 line-through">
                            ₹{product.price}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {product.status === "approved" ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <BsCheckCircleFill className="mr-1" /> Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                          <BsXCircleFill className="mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {editingProduct === product._id ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={decrementStock}
                            disabled={stockValue <= 0}
                            className="p-1 text-gray-500 hover:text-indigo-600 disabled:opacity-50"
                          >
                            <FiMinus />
                          </button>
                          <input
                            type="number"
                            value={stockValue}
                            onChange={(e) =>
                              handleStockChange(parseInt(e.target.value) || 0)
                            }
                            className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center"
                            min="0"
                          />
                          <button
                            onClick={incrementStock}
                            className="p-1 text-gray-500 hover:text-indigo-600"
                          >
                            <FiPlus />
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-900">
                          {product.stock} units
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      {editingProduct === product._id ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleSaveStock(product._id)}
                            className="p-1 text-green-600 hover:text-green-900"
                            title="Save"
                          >
                            <FiSave />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-red-600 hover:text-red-900"
                            title="Cancel"
                          >
                            <FiX />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(product)}
                          className="p-1 text-indigo-600 hover:text-indigo-900"
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No products found in your inventory
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 rounded-md border-l-4 border-blue-400 bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Inventory Tips
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                • Keep your inventory updated to avoid overselling
                <br />
                • Update stock levels immediately after fulfillment
                <br />• Low stock items will be highlighted automatically
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorInventory;
